import { useEffect, useMemo, useRef, useState } from "react";
import { client, errorText } from "../net/client";
import { useSignal } from "../net/signal";
import { VoiceButton } from "./VoiceButton";
import { Hub } from "./Hub";
import { Profile } from "./Profile";
import { Room } from "./Room";
import { Watch } from "./Watch";
import { ChatBar, ChatFab, ChatLive, ChatPanel } from "./Chat";
import { CHAT_TOGGLE } from "../game/chat";
import { CloseButton, GearButton, TableSettings, useFullscreen } from "./TableSettings";
import { HokmTable, HokmScorebar } from "../game/HokmTable";
import { CharBargTable, CharBargScorebar } from "../game/CharBargTable";
import { HaftTable, HaftScorebar } from "../game/HaftTable";
import { ShelemTable } from "../game/ShelemTable";
import { BoardTable } from "../game/BoardTable";
import { ArrowsTable } from "../arrows/Table";
import { OfflineLevel } from "../arrows/RaceView";
import { arrowsRun, freshStage, playBig, playStage } from "../arrows/StageStrip";
import { ANIMALS } from "../arrows/animals";
import { loadProgress } from "../arrows/stages";
import { MafiaTable } from "../game/MafiaTable";
import { quietNote } from "../mafia/text";
import { MaintenanceGate } from "./Maintenance";
import { loadTableArt } from "../game/tableArt";
import { applyCardBack } from "../game/cardBack";
import "../game/speed"; // ضریبِ سرعتِ صحنه را روی <html> می‌نویسد
import { carpetOfTable, showCarpet } from "../game/carpet";
import { fa } from "../game/cards";
import { Overlay } from "./Overlay";
import { useBackScreen } from "./screens";
import { authHref } from "./Account";
import { guestSeat, loadRoomInvite } from "../net/invite";
import { Dialogs, ask, askText, notify } from "./Dialog";
import { Cups } from "./Cups";
import { cupLine } from "./cup";
import { StakeIntro } from "../game/StakeIntro";
import { AdBreak, AdClock, playAfterAd } from "./Ads";
import { WheelButton } from "./Wheel";
import { armIntro, planFor } from "../game/stake";

export function App() {
  // صفحه‌ی خانه یا فهرستِ تماشا. حالتِ محلی است و نه مسیرِ نشانی: تماشا جایی
  // نیست که بشود لینکش را فرستاد — میز چند دقیقه بعد تمام شده است.
  // ‎?watch=1 از سایت (دکمه‌ی «تماشا»ی نوارِ بالا) مستقیم به فهرستِ میزها می‌آید.
  // ‎?watch=… ، ‎?cups=1 و ‎?cup=<id> از لندینگ می‌آیند: لینکِ «جدول‌ها» و کارتِ
  // هر لیگ باید مستقیم همان صفحه را باز کند، نه خانه‌ای که باید دوباره از آن
  // گشت.
  const [page, setPage] = useState<"home" | "watch" | "cups" | "profile">(() => {
    if (typeof location === "undefined") return "home";
    const q = new URLSearchParams(location.search);
    if (q.get("watch")) return "watch";
    if (q.get("cups") || q.get("cup") || q.get("cities")) return "cups";
    if (q.get("profile")) return "profile";
    // فروشگاه از ۲۲ اکتبرِ ۲۰۲۶ فقط /shop.html است (کاربر: «همه‌چیز در خودِ فروشگاه»)؛ لینک‌های
    // کهنه‌ی ‎?shop=… (برگشتِ پرداخت، آگهی‌ها، اپِ کهنه) همان‌جا می‌روند.
    if (q.get("shop")) location.replace("/shop.html");
    return "home";
  });
  const [chatPanel, setChatPanel] = useState(false);
  const [gear, setGear] = useState(false);
  // شناسه‌ی کارزاری که صفحه‌ی «لیگ و جام» با آن باز می‌شود: اول آن‌که لندینگ
  // خواسته (‎?cup=…)، و بعد هر بار که نوارِ جامِ لابی زده شود.
  const [initialCup, setInitialCup] = useState(() =>
    typeof location === "undefined" ? null : new URLSearchParams(location.search).get("cup"));
  // ‎?cities=1 از کارتِ «لیگ و جامِ شهرها» ِ لندینگ: بخشِ شهرها بالای فهرست.
  const [focusCities] = useState(() =>
    typeof location !== "undefined" && new URLSearchParams(location.search).get("cities") === "1");

  // اعلانِ سرور (پیامِ ادمین، پاسخِ پشتیبانی، اخطار): یک پیامِ گذرا روی صفحه؛
  // متنِ کامل در صندوق می‌مانَد.
  const lastNotice = useSignal(client.lastNotice);
  useEffect(() => { if (lastNotice) notify((lastNotice.kind === "warn" || lastNotice.kind === "maintenance" ? "⚠️ " : "✉️ ") + lastNotice.title); }, [lastNotice]);
  // لمسی که سرعت‌گیر کنار گذاشت (net/pace.ts) یا سرور «آرام‌تر» گفت: پیامِ گذرا، نه نوارِ ماندگار.
  const slowed = useSignal(client.slowed);
  useEffect(() => { if (slowed) notify(errorText("too_many")); }, [slowed]);
  // گوهرِ روزانه‌ی اشتراک سرِ اتصال می‌رسد؛ بی این پیام فقط عددِ کیف بی‌صدا بالا می‌رفت و
  // مشترک نمی‌فهمید وعده‌ی «هر روز» سرِ جایش است.
  const walletDelta = useSignal(client.walletDelta);
  useEffect(() => {
    if (walletDelta?.reason === "sub_daily" && walletDelta.deltaGems > 0n) {
      notify(`✦ ${fa(walletDelta.deltaGems)} گوهر امروز اشتراک به کیفت آمد`);
    }
  }, [walletDelta]);
  // خبرِ تعمیرات وسطِ بازی: نوارِ ماندگار، نه toastِ سه‌ثانیه‌ای و نه بیرون
  // انداختن. کسی که وسطِ دست است باید بتواند دستش را تمام کند و در همان حال
  // بداند که چه در راه است.
  const [maintSoon, setMaintSoon] = useState("");
  useEffect(() => {
    if (lastNotice?.kind === "maintenance") setMaintSoon(lastNotice.body || lastNotice.title || "سرویس به‌زودی برای به‌روزرسانی بسته می‌شود");
  }, [lastNotice]);

  // کدِ اتاق از خودِ نشانی: لینکی که دوستت می‌فرستد مستقیم سرِ همان اتاق می‌نشاند. حسابِ فعال
  // همان دم می‌پیوندد؛ بی‌حساب اول دروازه‌ی لینک را می‌بیند (چه کسی دعوتش کرده، نام) و با «شروع» ِ
  // همان‌جا می‌نشیند — [[InviteGate]]. کد تا آن وقت نگه داشته می‌شود.
  const pendingRoom = useRef<string | null>(null);
  const username = useSignal(client.username);
  const verified = useSignal(client.verified);
  const state = useSignal(client.state);
  // پشتِ کارتِ به‌کاربسته روی همه‌ی ورق‌های پشت‌رو — از پاسخِ ورود و «به‌کار ببند».
  const myBack = useSignal(client.back);
  useEffect(() => { applyCardBack(myBack); }, [myBack]);
  useEffect(() => {
    void client.connect();
    loadTableArt();
    const code = new URLSearchParams(location.search).get("room");
    if (code && /^\d{6}$/.test(code)) {
      pendingRoom.current = code;
      // چه کسی دعوت کرده، برای دروازه‌ی لابی؛ و بلیطش تا تأییدِ ایمیل — net/invite.ts.
      void loadRoomInvite(code);
    }
  }, []);
  // پیوستن به اتاقِ لینک: با حسابِ فعال، یا بی حساب وقتی بازیکن «شروع» ِ دروازه را زد ([[guestSeat]])
  const asGuest = useSignal(guestSeat);
  useEffect(() => {
    if (state !== "online" || !pendingRoom.current) return;
    if (!(username && verified) && !asGuest) return;
    client.joinRoom(pendingRoom.current);
    pendingRoom.current = null;
  }, [state, username, verified, asGuest]);

  // اتاقِ رمزدار: سرور که رمز خواست، همین‌جا پرسیده و دوباره فرستاده می‌شود.
  // در Hub نمی‌نشیند چون کدِ ‎?room= از لندینگ هم از همین راه می‌آید و آن
  // وقت هنوز هیچ صفحه‌ای باز نشده.
  const needPass = useSignal(client.needRoomPassword);
  useEffect(() => {
    if (!needPass) return;
    client.needRoomPassword.value = null;
    void (async () => {
      const pw = await askText({
        title: "این اتاق رمز دارد",
        body: "رمزی که میزبان گذاشته را وارد کن.",
        ok: "پیوستن",
        secret: true,
        placeholder: "رمز اتاق",
        maxLength: 64,
      });
      if (pw) client.joinRoom(needPass, pw);
    })();
  }, [needPass]);

  // ‎?watch=<شناسه‌ی میز> از لندینگ («همین حالا سرِ میز»): وصل که شدیم، تماشای همان
  // میز — بی‌حساب، چون تماشا آزاد است. «?watch=1» فقط فهرست را باز می‌کند.
  const pendingWatch = useRef<string | null>(
    (() => { const w = typeof location !== "undefined" ? new URLSearchParams(location.search).get("watch") : null; return w && w !== "1" ? w : null; })());
  useEffect(() => {
    if (!pendingWatch.current) return;
    // تماشا حساب نمی‌خواهد ولی سرور را می‌خواهد: مرورگرِ بی‌حساب همین‌جا مهمانش را می‌گیرد.
    if (state === "signedOut") { void client.connect({ create: true }); return; }
    if (state !== "online") return;
    // کارتِ آگهیِ پیش از تماشا، هر بار. لودرِ صفحه‌ی تازه هنوز سرد است و کارت خودش مهلتِ
    // بیشتری می‌گیرد (COLD_FILL_MS)؛ اگر پیکربندی هنوز نرسیده بود، بی کارت — هرگز انتظار.
    const id = pendingWatch.current;
    pendingWatch.current = null;
    playAfterAd(() => client.watchTable(id), "watch");
  }, [state]);

  const table = useSignal(client.table);
  const cb = useSignal(client.cbTable);
  const sh = useSignal(client.shTable);
  const hk = useSignal(client.hkTable);
  const bd = useSignal(client.board);
  const mf = useSignal(client.mafia);
  const room = useSignal(client.room);
  const watching = useSignal(client.watching);
  // شلم هم: بی آن، میزِ شلمِ وب نه چت داشت، نه صدا، نه پرسشِ «بازگشت» ِ گوشی، نه پرده‌ی شرط —
  // App آن را «سرِ میز» نمی‌شمرد (کاربر، ۲۸ اکتبر ۲۰۲۶).
  const inGame = !!(table ?? cb ?? sh ?? hk ?? bd ?? mf);

  // نتیجه‌ی میزِ جام معمولاً روی پرده‌ی پایانِ مسابقه دیده می‌شود ([[CupAfter]]).
  // ولی اگر بازیکن پیش از رسیدنش از میز بیرون آمده بود، دست‌کم یک خبرِ گذرا.
  const cupUpdate = useSignal(client.cupUpdate);
  useEffect(() => {
    if (cupUpdate && !inGame) notify("🏅 " + cupLine(cupUpdate));
    // فقط با رسیدنِ نتیجه‌ی تازه؛ بیرون‌آمدن از میز خودش خبری نیست.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cupUpdate]);

  /**
   * پرده‌ی شرطِ آغازِ میز.
   *
   * با شناسه‌ی میز کلید می‌خورد و نه با خودِ وضعیت: یک میز یک پرده دارد، و
   * هر وضعیتِ بعدیِ همان میز باید از کنارش رد شود. `armIntro` هم همین را
   * دوباره می‌سنجد، پس رندرِ دوباره و StrictMode بی‌خطرند.
   *
   * این‌جا و نه در خودِ میز، چون باید **پیش از** سوارشدنِ صحنه دروازه بسته
   * شده باشد؛ اثرهای فرزند بعد از رندرِ پدر اجرا می‌شوند و کنترلر همان‌جا
   * `introGate()` را می‌گیرد.
   */
  const tableId = table?.tableId ?? cb?.tableId ?? sh?.tableId ?? hk?.tableId ?? bd?.tableId ?? mf?.tableId ?? "";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const intro = useMemo(() => armIntro(planFor(table ?? cb ?? hk ?? sh)), [tableId]);
  // رویه‌ی میز را سرور قرعه می‌کشد و در وضعیت می‌فرستد — با هر وضعیتِ تازه هم‌گام
  const carpetKey = carpetOfTable(table, cb, sh, hk, bd, mf, room);
  useEffect(() => { showCarpet(carpetKey); }, [carpetKey]);

  // چت فقط سرِ میز معنا دارد — چه نشسته باشی چه تماشاچی. در اتاق هنوز میزی
  // نیست که پیام روی آن بنشیند.
  const chatOpen = table?.chatOpen ?? cb?.chatOpen ?? sh?.chatOpen ?? hk?.chatOpen ?? bd?.chatOpen ?? mf?.chatOpen ?? false;
  const isHost = table?.host ?? cb?.host ?? sh?.host ?? hk?.host ?? bd?.host ?? mf?.host ?? false;
  useEffect(() => { if (!inGame) setChatPanel(false); }, [inGame]);
  // برگه‌ی ⚙ با رفتن از میز بسته می‌شود — لابی سربرگِ خودش را دارد.
  const leftTable = !(inGame || sh || room || watching);
  useEffect(() => { if (leftTable) setGear(false); }, [leftTable]);
  // دکمه‌های چتِ پایینِ میز (ChatFab، BoardChatButtons، نوارِ تماشاچی) همین برگه را باز و بسته می‌کنند
  useEffect(() => {
    const toggle = () => setChatPanel((v) => !v);
    window.addEventListener(CHAT_TOGGLE, toggle);
    return () => window.removeEventListener(CHAT_TOGGLE, toggle);
  }, []);

  /**
   * «بازگشت»ِ گوشی، لایه به لایه.
   *
   * ترتیب همان ترتیبِ باز شدن است: برگه‌ی چت بعد از میز باز می‌شود، پس اول
   * بسته می‌شود. سرِ میز، بازگشت همان پرسشِ دکمه‌ی «بیرون» را می‌پرسد و اگر
   * نخواستی، ورودیِ تاریخچه دوباره گذاشته می‌شود و میز سرِ جایش می‌مانَد —
   * وگرنه یک اشاره‌ی لبه وسطِ دست، بازی را می‌بُرد.
   */
  useBackScreen(page !== "home", () => setPage("home"));
  useBackScreen(!!watching, () => client.stopWatch());
  useBackScreen(!watching && !!(inGame || room), () => {
    // پنجره‌ی خودمان ناهم‌زمان است؛ لایه می‌مانَد (true) و اگر «بیرون» گفت، می‌رویم.
    void leaveTable();
    return true;
  });
  // مسابقه‌ی آفلاینِ فلش‌ها روی لابی باز است؛ بازگشت به همان لابی می‌رود — [[OfflineRace]].
  const run = useSignal(arrowsRun);
  useBackScreen(!!run, () => { arrowsRun.value = null; });
  // برگه‌ی چت «بازگشت» را از Overlay ِ خودش می‌گیرد؛ این‌جا دوباره ثبت نمی‌شود،
  // وگرنه دو ورودیِ تاریخچه می‌ماند و یک بازگشت دو لایه می‌بست.

  // قابِ صفحه روی دسکتاپ (lux.css، «صفحه‌ی پهن»): لابی پهن و دوستونی، میز بزرگ‌تر از قابِ گوشی.
  const atTable = inGame;
  // سربرگِ میز: نام، ⚙ و ✕ — در اتاق و حینِ تماشا هم.
  const tableMode = atTable || !!room || !!watching;
  // تخته‌ای‌ها و مافیا چت را کنارِ کنترل‌های پایینِ خودشان دارند (منچ و دومینو با ایموجی، بقیه
  // ChatFab ِ inline)؛ میزهای ورق دکمه‌ی گردِ پایین-راست را می‌گیرند.
  const ownChat = !!(bd || mf);

  // مسابقه‌ی آفلاینِ فلش‌ها: تمام‌صفحه، با ✕ و صدای خودش. برگشت به لابی روی همان بازی؛ اگر مرحله‌ی تازه‌ای باز
  // شد، در نوار با جهش پیدا می‌شود.
  if (run && !inGame && !room) {
    const back = () => {
      const lv = loadProgress().level;
      freshStage.value = lv > run.stage ? lv : null;
      arrowsRun.value = null;
    };
    if (run.big) {
      const k = ANIMALS.findIndex((a) => a.id === run.big);
      return (
        <OfflineLevel key={run.n} stage={-1} seed={run.seed} big={run.big}
          onAgain={() => playBig(run.big!)} onNext={() => playBig(ANIMALS[(k + 1) % ANIMALS.length]!.id)}
          onMenu={() => { arrowsRun.value = null; }} />
      );
    }
    return (
      <OfflineLevel key={run.n} stage={run.stage} seed={run.seed}
        onAgain={() => playStage(run.stage)} onNext={() => playStage(run.stage + 1)} onMenu={back} />
    );
  }

  return (
    <div className={"shell " + (atTable ? "attable" : "lobby")}>
      {/* بالاتر از همه‌چیز: تا وقتی سرور بسته است، هیچ دکمه‌ای معنا ندارد. */}
      <MaintenanceGate />
      {maintSoon && (
        <div className="maintsoon" role="status">
          <span>{maintSoon}</span>
          <button type="button" onClick={() => setMaintSoon("")} aria-label="بستن">×</button>
        </div>
      )}
      <header className="hdr">
        {/* راهِ برگشت به صفحه‌ی معرفی. سرِ میز یا در اتاق پیوند نیست: یک لمسِ
            اشتباه نباید وسطِ دست بازیکن را از بازی بیرون ببرد — راهِ بیرون
            رفتن همان ✕ است که می‌پرسد. */}
        {tableMode
          ? <h1>بازی وان</h1>
          : <h1><a href="/" title="صفحه‌ی بازی وان">بازی وان</a></h1>}
        <div className="spacer" />
        {tableMode ? (
          // سرِ میز سه چیز و بس (کاربر، ۲۵ اکتبر ۲۰۲۶: «دکمه‌های سربرگ زیاد است»): سرعت، صدا،
          // صدای تاس و تمام‌صفحه در ⚙؛ چت، ایموجی و میکروفون پایینِ میز.
          <>
            <GearButton onClick={() => setGear(true)} />
            {watching
              // تماشاچی چیزی را رها نمی‌کند، پس پرسیدن هم ندارد.
              ? <CloseButton label="پایان تماشا" onClick={() => client.stopWatch()} />
              : <CloseButton label="بیرون از میز" onClick={() => void leaveTable()} />}
          </>
        ) : (
          <>
            {/* گردونه در سربرگ و نه وسطِ لابی (۲۰ اکتبر ۲۰۲۶)؛ سرِ میز نه — آن‌جا جای بازی است. */}
            <WheelButton />
            <FullscreenButton />
            {username ? (
              // ثبت‌شده: نامش و «خروج». تأییدنشده: به جای تغییرِ رمز، «تأییدِ ایمیل».
              // نامش، و بس. خروج و تغییرِ رمز و پشتیبانی همه در لندینگ‌اند:
              // این صفحه فقط برای چیدنِ میز و بازی است.
              verified
                ? <span className="who" dir="ltr" translate="no">@{username}</span>
                : <a className="hb cta" href={authHref("login")}>تأیید ایمیل</a>
            ) : (
              // مهمان: یک دکمه و نه دو تا. صفحه‌ی حساب خودش هر دو حالت را
              // دارد و همان‌جا می‌شود بینشان رفت، پس دو دکمه فقط جای نوار را
              // می‌گرفت — و روی گوشی جا تنگ است. لندینگ و صندوق از اول همین
              // یک دکمه را داشتند.
              <a className="hb cta" href={authHref("login")}>ورود / ثبت‌نام</a>
            )}
          </>
        )}
      </header>

      <ConnBar />
      <WatchBar watchers={table?.watchers ?? cb?.watchers ?? hk?.watchers ?? bd?.watchers ?? mf?.watchers ?? 0} watching={!!watching} />

      {table && <HokmScorebar state={table} />}
      {cb && <CharBargScorebar state={cb} />}
      {hk && <HaftScorebar state={hk} />}

      {table ? <HokmTable state={table} />
        : cb ? <CharBargTable state={cb} />
        : hk ? <HaftTable state={hk} />
        : sh ? <ShelemTable state={sh} />
        // تخته‌ای‌ها یک نما دارند؛ میزِ تازه (شناسه‌ی دیگر) صحنه‌ی تازه می‌خواهد.
        : bd ? (bd.game === "arrows" ? <ArrowsTable key={bd.tableId} state={bd} /> : <BoardTable key={bd.tableId} state={bd} />)
        // مافیا: نمای همین بازیکن و جریانِ خودش — src/mafia/controller.ts
        : mf ? <MafiaTable key={mf.tableId} state={mf} />
        : room ? <Room room={room} />
        : page === "watch" ? <Watch onBack={() => setPage("home")} />
        : page === "cups" ? <Cups onBack={() => setPage("home")} initial={initialCup} focusCities={focusCities} />
        : page === "profile" ? <Profile onBack={() => setPage("home")} />
        // «تماشا» و «لیگ و جام» از لابی برداشته شدند — جایشان لندینگ است و
        // همان‌جا هم غنی‌ترند. صفحه‌هایشان می‌مانند، چون لینک‌های لندینگ
        // (‎?watch=… ، ‎?cups=1 ، ‎?cup=…) مستقیم به همین‌جا می‌آیند. تنها
        // استثنا نوارِ جامِ آرناست ([[CupStrip]]): جام بی خبرِ شروع جمع نمی‌شود.
        : <Hub onProfile={() => setPage("profile")}
               onCup={(id) => { setInitialCup(id); setPage("cups"); }} />}

      {/* جریانِ زنده‌ی پیام‌ها روی میز. تماشاچی نوارِ چت هم دارد، پس جریانش
          پایین‌تر می‌نشیند؛ بازیکن دستش آن‌جاست و جریان بالاتر می‌مانَد. */}
      {/* مافیا جریانش را خودش بالای تابلوی پایین می‌کشد — MafiaTable */}
      {inGame && !chatPanel && !mf && <ChatLive raised={!!watching} />}
      {/* میکروفون پایین-چپ (پورتال، position: fixed) و چت پایین-راست */}
      {inGame && !watching && <VoiceButton />}
      {inGame && !watching && !chatPanel && !ownChat && <ChatFab />}
      {/* مافیا: شب برای تماشاچی هم چت بسته است — نوار می‌گوید شب است، نه «بسته است» */}
      {inGame && watching && !chatPanel && <ChatBar chatOpen={chatOpen} closedNote={mf ? quietNote(mf) : ""} />}
      {inGame && (
        <ChatPanel open={chatPanel} onClose={() => setChatPanel(false)} chatOpen={chatOpen} host={isHost} />
      )}
      {gear && tableMode && (
        <TableSettings sound={!!(bd || mf)} dice={!!bd && DICE_GAMES.has(bd.game)} onClose={() => setGear(false)} />
      )}
      {intro && <StakeIntro key={intro.tableId} plan={intro} />}
      <KickedVeil />
      <AdBreak />
      <AdClock atTable={atTable} inRoom={!!room} />
      <Dialogs />
    </div>
  );
}

/** «بیرون» از میز یا اتاق — با پرسش، چون وسطِ دست برگشت‌ناپذیر است. */
async function leaveTable() {
  const yes = await ask({
    title: "از میز بیرون بروی؟",
    body: "جای تو تا مدتی ربات بازی می‌کند؛ اگر زود برگردی، سر همان میز می‌نشینی.",
    ok: "بیرون بروم",
    cancel: "می‌مانم",
    danger: true,
  });
  if (yes) client.leaveRoom();
}

/**
 * تمام‌صفحه.
 *
 * روی گوشی، نوارِ نشانیِ مرورگر ته صفحه را می‌خورد — دقیقاً جایی که دستِ
 * بازیکن است. تمام‌صفحه آن نوار را می‌برد و میز همان قدِ اپ را می‌گیرد. iOS
 * سافاری این API را برای صفحه‌ی وب ندارد (فقط برای ویدئو)؛ آن‌جا دکمه
 * نمی‌آید تا دکمه‌ی بی‌کار نشان ندهیم.
 */
function FullscreenButton() {
  const fs = useFullscreen();
  if (!fs) return null;
  return (
    <button className="fs" onClick={fs.toggle} title={fs.on ? "خروج از تمام‌صفحه" : "تمام‌صفحه"} aria-label={fs.on ? "خروج از تمام‌صفحه" : "تمام‌صفحه"}>
      {/* SVG و نه «⤢»: آن نویسه از بلوکِ پیکان‌های کمیاب است و روی هر
          دستگاهی نیست — جایی مربعِ خالی می‌شد، جایی نازک و بی‌رنگ. */}
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {fs.on
          ? <path d="M9 3v6H3M15 21v-6h6M3 15h6v6M21 9h-6V3" />
          : <path d="M3 9V3h6M21 15v6h-6M15 3h6v6M9 21H3v-6" />}
      </svg>
    </button>
  );
}

const stateLabel: Record<string, string> = {
  offline: "آفلاین",
  loggingIn: "در حال ورود…",
  connecting: "در حال اتصال…",
  online: "آنلاین",
  reconnecting: "در حال بازگشت…",
  signedOut: "",
};

/**
 * صفحه‌ای که کنار گذاشته شده — روی میز، نه یک نوارِ باریک بالای صفحه.
 *
 * خرابیِ واقعی که این را آورد: تماشاچی میزی می‌دید که وسطِ یک دست یخ زده —
 * دو کارت روی میز که دیگر تکان نمی‌خوردند. میز کاملاً **زنده** به نظر
 * می‌رسید و فقط «متوقف»؛ آن نوارِ باریکِ بالا هم لابه‌لای امتیاز و نامِ
 * صندلی‌ها گم می‌شد. نتیجه این بود که آدم فکر می‌کرد بازی قفل کرده، نه
 * این‌که صفحه‌اش کنار گذاشته شده.
 *
 * و راهِ برگشت هم نداشت: تنها چاره تازه‌کردنِ صفحه بود. حالا یک دکمه هست —
 * با کلیکِ آدم و نه خودکار، چون آن یکی زبانه هم همین کار را می‌کند و دو تا
 * با هم بی‌پایان همدیگر را بیرون می‌اندازند.
 */
function KickedVeil() {
  const kicked = useSignal(client.kicked);
  if (!kicked) return null;
  return (
    <Overlay base="veil" label="این صفحه کنار گذاشته شد">
      <div className="box">
        <b>این صفحه کنار گذاشته شد</b>
        <p className="muted">
          همین حساب جای دیگری باز شد — یک زبانه‌ی دیگر، یا گوشی‌ات. هر حساب
          در هر لحظه فقط یک جا می‌تواند باشد، پس این صفحه دیگر خبری نمی‌گیرد.
        </p>
        <button className="btn" onClick={() => client.takeOver()}>
          همین‌جا ادامه بده
        </button>
        <p className="muted small">آن یکی صفحه آن‌وقت کنار گذاشته می‌شود.</p>
      </div>
    </Overlay>
  );
}

/** نوارِ وضعیت: حالتِ واقعی و آخرین خطا — نه یک «در حالِ اتصال»ِ همیشگی. */
function ConnBar() {
  const state = useSignal(client.state);
  const err = useSignal(client.lastError);
  // بی‌حساب خطا نیست: دروازه‌ی ثبت‌نام خودش می‌گوید چه کنی.
  if ((state === "online" || state === "signedOut") && !err) return null;
  const dot = state === "online" ? "online" : state === "offline" ? "off" : "busy";
  return (
    <div className="conn">
      <i className={`dot ${dot}`} />
      <span>{stateLabel[state]}</span>
      {err && <span className="err">— {err}</span>}
    </div>
  );
}

/**
 * نوارِ تماشا — یک نوار، دو مخاطب.
 *
 * برای بازیکن: «دو نفر دارند نگاه می‌کنند». کسی که سرِ میزِ عمومی نشسته باید
 * بداند تماشا می‌شود، نه این‌که بعداً بفهمد.
 *
 * برای تماشاچی: «در حالِ تماشا» و بس — «دستِ کسی پیدا نیست» را کاربر ۲۴ اکتبر
 * ۲۰۲۶ برداشت.
 */
function WatchBar({ watchers, watching }: { watchers: number; watching: boolean }) {
  if (!watching && watchers <= 0) return null;
  return (
    <div className="conn">
      <span aria-hidden>👁</span>
      {watching
        ? <span>در حال تماشا{watchers > 1 ? ` · ${fa(watchers)} تماشاچی` : ""}</span>
        : <span>{fa(watchers)} نفر تماشا می‌کنند</span>}
    </div>
  );
}

/** بازی‌های تاس‌دار — ردیفِ «صدای تاس» ِ برگه‌ی ⚙ فقط سرِ میزِ همین‌ها. */
const DICE_GAMES = new Set(["mench", "nard", "marpele"]);
